import { useState } from "react"
import { Button, Badge, Separator, Card } from "@city-sage/aurum-avis-labs-components";
import { Stop } from "@city-sage/core-client";
import { getDayColor } from "../../lib/constants/dayColors";
import { Image } from "@city-sage/aurum-avis-labs-components";
import { Clock, MapPin, Star, Navigation, MoreHorizontal, Eye } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ShareButton } from "../actions/ShareButton";
import { LikeButton } from "../actions/LikeButton";
import { CommentButton } from "../actions/CommentButton";
import { Link } from "@tanstack/react-router";
import { useNavigationModalHandler } from "../../lib/hooks/useNavigation";
import { NavigationProviderModal } from "../actions/NavigationProviderModal";
import { useReactions } from "../../lib/useReactions";
import { MarkdownRenderer } from "../MarkdownRenderer";

interface ItineraryStopSectionProps {
  stop: Stop
  stopIndex: number
  dayIndex: number
}

export const ItineraryStopSection = ({ stop, stopIndex, dayIndex }: ItineraryStopSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dayColorConfig = getDayColor(dayIndex);
  const navigation = useNavigationModalHandler();

  // Use real reactions instead of mock data
  const { 
    isLiked, 
    likeCount, 
    toggleLike,
    isLoading: isLoadingReactions,
    isPending: isReactionPending
  } = useReactions({
    reactableType: 'stop',
    reactableId: stop.id
  });

  const formatTime = (time: string) => {
    if (!time) return null;
    return time.slice(0, 5); // Convert "HH:MM:SS" to "HH:MM"
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

    // Show highlight or description with line-clamp-3
  const displayDescription = stop.highlight || stop.description;

  const handleGetDirections = (): void => {
    if (stop.place.coordinates) {
      const formatAddress = () => {
        if (!stop.place.address) return '';
        const parts = [
          stop.place.address.addressLine1,
          stop.place.address.addressLine2,
          stop.place.address.city,
          stop.place.address.state,
          stop.place.address.postalCode,
          stop.place.address.countryName
        ].filter(Boolean);
        return parts.join(', ');
      };

      navigation.navigate({
        latitude: stop.place.coordinates.latitude,
        longitude: stop.place.coordinates.longitude,
        title: stop.title,
        address: formatAddress()
      });
    }
  };
  
  return (
    <Card className="overflow-hidden bg-white border-0  mb-4">

        {/* Image Section - Compact on mobile */}
        <div className="relative h-36">
          {stop.imageUrl ? (
                        <>
              <Image 
                src={stop.imageUrl} 
                image={stop.image}
                alt={stop.title} 
                className="w-full h-full"
                imageClassName="w-full h-full object-cover" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </>            
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-gray-400" />
            </div>
          )}

        </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Title and Location */}
        <div className="flex items-center gap-3">
           {/* Stop Number Badge - Floating */}
          <div className={`w-8 h-8 ${dayColorConfig.bgClass} rounded-full flex items-center justify-center shadow-lg`}>
            <span className="text-white font-bold text-xs">{stopIndex + 1}</span>
          </div>

          <h2 className="text-lg font-bold text-gray-900 leading-tight">{stop.title}</h2>
        </div>

        {stop.place.address && (
            <div className="flex items-start gap-2 text-sm text-gray-600 mt-3 mb-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{stop.place.address.addressLine1}</div>
                <div className="text-gray-500">
                  {stop.place.address.city}, {stop.place.address.countryName}
                </div>
              </div>
            </div>
          )}

        {/* Quick Actions Bar */}
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-4">
            <LikeButton
              isLiked={isLiked}
              likeCount={likeCount}
              onToggleLike={toggleLike}
              variant="light"
              layout="horizontal"
              size="sm"
            />

            <CommentButton
              postableType="stop"
              postableId={stop.id}
              variant="light"
              layout="horizontal"
              size="sm"
            />
          </div>

          {/* Share Button */}
          <ShareButton
            url={`${window.location.origin}/stops/${stop.id}`}
            title={stop.title}
            description={displayDescription}
            hashtags={['CitySage', 'Travel', 'Stop', stop.title.replace(/\s+/g, '')]}
            variant="light"
            layout="horizontal"
            size="sm"
            shareMethod="native"
          />
        </div>


        {/* Description */}
        {displayDescription && (
          <div className="space-y-3 mt-2">
            <div className="text-gray-700 text-sm leading-relaxed">
              <MarkdownRenderer content={displayDescription} className="line-clamp-3" />
            </div>
          </div>
        )}

        {/* Timing Details - Compact */}
        {(stop.startTime || stop.durationMinutes) && (
          <div className={`${dayColorConfig.lightBg} mt-2 rounded-2xl p-3`}>
            <div className="flex items-center gap-4 text-sm">
              <div className={`flex items-center gap-2 ${dayColorConfig.textClass}`}>
                <Clock className="w-4 h-4" />
                <span className="font-medium">{t('itinerary.stop.timing')}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                {stop.startTime && <span className="font-medium">{formatTime(stop.startTime)}</span>}
                {stop.startTime && stop.durationMinutes && <span className="text-gray-400">•</span>}
                {stop.durationMinutes && <span>{formatDuration(stop.durationMinutes)}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Mobile Optimized */}
        <div className="grid grid-cols-2 gap-3 pt-2 mt-2">
            <Button
              size="sm"
              onClick={() => navigate({ 
                to: "/itineraries/$itineraryId/stops/$stopId", 
                params: { itineraryId: stop.itineraryId, stopId: stop.id } 
              })}
            >
              <Eye className="w-4 h-4 mr-2" />
              {t('itinerary.stop.viewDetails')}
            </Button>

            <Button 
              variant="outline"
              size="sm"
              onClick={handleGetDirections}
            >
              <Navigation className="w-4 h-4 mr-2" />
              {t('itinerary.stop.directions')}
            </Button>
        </div>
      </div>

      {/* Navigation Provider Modal */}
      <NavigationProviderModal
        isOpen={navigation.isModalOpen}
        onClose={() => navigation.setIsModalOpen(false)}
        onSelect={navigation.handleProviderChoice}
        destinationName={stop?.title}
      />
    </Card>
  )
} 